import { ChevronDown, LogOut } from 'lucide-react'
import { useState } from 'react'
import { Badge, Skeleton } from '@amglobal/ui-shared'
import { useLogoutMutation } from '@/features/auth/hooks/use-logout-mutation'
import { useWorkspaceSession } from '@/features/auth/hooks/use-workspace-session'

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return (parts[0]?.[0] ?? '?').toUpperCase()
  return `${parts[0]?.[0] ?? ''}${parts[parts.length - 1]?.[0] ?? ''}`.toUpperCase()
}

export function UserMenu() {
  const [open, setOpen] = useState(false)
  const session = useWorkspaceSession()
  const logout = useLogoutMutation()

  const name = session.displayName
  const roleLabel = session.roleLabel

  if (session.isLoading && !session.snapshot) {
    return <Skeleton className="h-10 w-36 rounded-lg" />
  }

  return (
    <div className="relative">
      <button
        type="button"
        className="flex items-center gap-2 rounded-lg border border-border-default bg-surface-card py-1.5 pl-1.5 pr-2 text-sm shadow-sm hover:bg-surface-workspace"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="menu"
      >
        <span className="flex h-8 w-8 items-center justify-center rounded-md bg-brand-primary text-xs font-bold text-white">
          {initials(name)}
        </span>
        <span className="hidden max-w-[120px] truncate text-left sm:block">
          <span className="block font-semibold text-text-primary">{name}</span>
          {roleLabel ? (
            <span className="block truncate text-xs text-text-muted">{roleLabel}</span>
          ) : null}
        </span>
        <ChevronDown className="h-4 w-4 text-text-muted" aria-hidden />
      </button>

      {open ? (
        <>
          <button
            type="button"
            className="fixed inset-0 z-40 cursor-default bg-transparent"
            aria-label="Close menu"
            onClick={() => setOpen(false)}
          />
          <div
            className="absolute right-0 top-full z-50 mt-2 w-56 rounded-xl border border-border-default bg-surface-elevated p-2 shadow-lg"
            role="menu"
          >
            <div className="border-b border-border-default px-3 py-2">
              <p className="truncate text-sm font-semibold text-text-primary">{name}</p>
              {session.tenantLabel !== '—' ? (
                <Badge variant="default" className="mt-1">
                  {session.tenantLabel}
                </Badge>
              ) : null}
            </div>
            <button
              type="button"
              role="menuitem"
              className="mt-1 flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-text-primary hover:bg-surface-workspace disabled:opacity-50"
              disabled={logout.isPending}
              onClick={() => {
                setOpen(false)
                void logout.mutateAsync()
              }}
            >
              <LogOut className="h-4 w-4" aria-hidden />
              Sign out
            </button>
          </div>
        </>
      ) : null}
    </div>
  )
}

export function TenantSwitcher() {
  const session = useWorkspaceSession()

  if (session.isLoading && !session.snapshot) {
    return <Skeleton className="hidden h-10 w-40 rounded-lg md:block" />
  }

  return (
    <div className="hidden items-center gap-2 rounded-lg border border-border-default bg-surface-card px-3 py-2 text-sm md:flex">
      <span className="text-text-muted">Tenant</span>
      <span className="max-w-[160px] truncate font-semibold text-text-primary">
        {session.tenantLabel}
      </span>
    </div>
  )
}
